import { clsx } from 'clsx'
import loftIcon from '@/assets/icons/ndt-icons/loft_nolabel.svg?raw'
import arcticIcon from '@/assets/icons/inspection-icons/arctic.svg?raw'
import etIcon from '@/assets/icons/ndt-icons/et_nolabel.svg?raw'
import mtIcon from '@/assets/icons/ndt-icons/mt_nolabel.svg?raw'
import ptIcon from '@/assets/icons/ndt-icons/pt_nolabel.svg?raw'
import vtIcon from '@/assets/icons/ndt-icons/VT_nolabel.svg?raw'
import ltIcon from '@/assets/icons/ndt-icons/lt_nolabel.svg?raw'
import utIcon from '@/assets/icons/ndt-icons/ut_nolabel.svg?raw'
import rtIcon from '@/assets/icons/ndt-icons/rt_nolabel.svg?raw'
import threadIcon from '@/assets/icons/ndt-icons/thread_nolabel.svg?raw'
import wrenchIcon from '@/assets/icons/ndt-icons/wrench_nolabel.svg?raw'

// Cut-out details in the icons must match the tile background (--color-primary).
const TILE_BG = '#1E3A5F'
const WLL_YELLOW = '#FFBB00'

const recolor = (svg: string) =>
  svg
    .replace(/^<svg[^>]*>/, (tag) => tag.replace(/\s(width|height)="[^"]*"/g, ''))
    .replace(/(fill|stroke)="(white|#FFFFFF|#FEFEFE|#F8FAFC)"/gi, `$1="${TILE_BG}"`)
    .replace(/(fill|stroke)="#(BDBDBD|B8B8B8|AEAEAE|D9D9D9|EBEBEB|BA6363)"/gi, '$1="currentColor"')

export interface NdtShortcut {
  name: string
  label: string
  description?: string
  svg: string
  group: 'lifting' | 'ndt'
  kind?: 'sakkyndig' | 'sammeliste'
  hidden?: boolean
}

const ALL_SHORTCUTS: NdtShortcut[] = [
  { name: 'Periodisk', label: 'Periodisk kontroll', description: 'Sakkyndig inspeksjon', svg: recolor(loftIcon), group: 'lifting', kind: 'sakkyndig' },
  { name: 'Samleliste', label: 'Løst utstyr', description: 'Samleliste', svg: recolor(arcticIcon), group: 'lifting', kind: 'sammeliste', hidden: true },
  { name: 'SR', label: 'Service', description: 'Servicerapport', svg: recolor(wrenchIcon), group: 'lifting' },
  { name: 'ET', label: 'ET', svg: recolor(etIcon), group: 'ndt' },
  { name: 'MPI', label: 'MPI', svg: recolor(mtIcon), group: 'ndt' },
  { name: 'PT', label: 'PT', svg: recolor(ptIcon), group: 'ndt' },
  { name: 'VT', label: 'VT', svg: recolor(vtIcon), group: 'ndt' },
  { name: 'LT', label: 'LT', svg: recolor(ltIcon), group: 'ndt' },
  { name: 'UT', label: 'UT', svg: recolor(utIcon), group: 'ndt' },
  { name: 'RT', label: 'RT', svg: recolor(rtIcon), group: 'ndt' },
  { name: 'Thread', label: 'Gjenger', svg: recolor(threadIcon), group: 'ndt' },
]

export const NDT_SHORTCUTS = ALL_SHORTCUTS.filter((s) => !s.hidden)

interface NdtShortcutIconProps {
  shortcut: NdtShortcut
  color: string
  className?: string
}

export const NdtShortcutIcon = ({ shortcut, color, className }: NdtShortcutIconProps) => (
  <span
    aria-hidden
    className={clsx('flex items-center justify-center [&>svg]:max-w-full [&>svg]:max-h-full [&>svg]:w-auto [&>svg]:h-full', className)}
    style={{ color }}
    dangerouslySetInnerHTML={{ __html: shortcut.svg }}
  />
)

interface ShortcutTileProps {
  shortcut: NdtShortcut
  onSelect: (shortcut: NdtShortcut) => void
  size?: 'md' | 'lg'
}

export const ShortcutTile = ({ shortcut, onSelect, size = 'md' }: ShortcutTileProps) => (
  <button
    onClick={() => onSelect(shortcut)}
    className={clsx(
      'flex flex-col items-center justify-center rounded-lg shadow-sm transition-all hover:shadow-md hover:ring-2 hover:ring-[#FFBB00]/60 active:scale-95',
      size === 'lg' ? 'gap-3 h-36 sm:h-44' : 'gap-1.5 h-20',
    )}
    style={{ backgroundColor: TILE_BG }}
  >
    <NdtShortcutIcon
      shortcut={shortcut}
      color={WLL_YELLOW}
      className={size === 'lg' ? 'h-16 sm:h-20 w-24' : 'h-8 w-10'}
    />
    <span className="text-center">
      <span className={clsx('block font-semibold text-white', size === 'lg' ? 'text-base' : 'text-xs')}>
        {shortcut.label}
      </span>
      {size === 'lg' && shortcut.description && (
        <span className="block text-sm text-white/60 mt-0.5">{shortcut.description}</span>
      )}
    </span>
  </button>
)
